import { useCallback, useState } from 'react';

export interface UseCsvDownloadOptions {
  /**
   * Callback invoked when an error occurs during download.
   */
  onError?: (error: Error) => void;
}

export interface UseCsvDownloadResult {
  /**
   * Triggers a CSV download with the given filename and content.
   * @param filename - The name of the file to download (including .csv extension)
   * @param csvContent - The CSV content as a string
   */
  downloadCsv: (filename: string, csvContent: string) => void;
  /**
   * Whether a download is currently in progress.
   */
  isDownloading: boolean;
}

/**
 * Hook for downloading CSV files using the Blob API.
 *
 * Handles:
 * - Blob creation and URL management
 * - Loading state with proper cleanup via try/finally
 * - Deferred URL revocation to avoid racing with download initiation
 * - Error handling
 *
 * @example
 * const { downloadCsv, isDownloading } = useCsvDownload({
 *   onError: (error) => toast(ToastTypeVariants.ERROR, 'Failed to download CSV')
 * });
 *
 * // Later, when user clicks download button:
 * downloadCsv('report.csv', csvContent);
 */
export function useCsvDownload(options: UseCsvDownloadOptions = {}): UseCsvDownloadResult {
  const { onError } = options;
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadCsv = useCallback(
    (filename: string, csvContent: string) => {
      setIsDownloading(true);

      try {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const href = URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');

        anchor.href = href;
        anchor.style.display = 'none';
        anchor.download = filename;

        window.document.body.appendChild(anchor);
        anchor.click();

        // Defer cleanup to avoid racing with download initiation in some browsers
        setTimeout(() => {
          URL.revokeObjectURL(href);
          anchor.remove();
        }, 100);
      } catch (error) {
        if (onError && error instanceof Error) {
          onError(error);
        } else {
          // Re-throw if no error handler provided
          throw error;
        }
      } finally {
        setIsDownloading(false);
      }
    },
    [onError],
  );

  return {
    downloadCsv,
    isDownloading,
  };
}
